<template>
  <div id="platform-admin-dashboard">
    <header>
      <h1>Visão geral</h1>
      <p>Métricas agregadas de todos os tenants do Fluxoo DOC.</p>
    </header>
    <div class="tiles">
      <template v-if="totals">
        <div class="item">
          <h1>Tenants</h1>
          <p>{{ totals.tenants }}</p>
          <h2>Contas ativas no sistema.</h2>
        </div>
        <div class="item">
          <h1>Usuários</h1>
          <p>{{ totals.users }}</p>
          <h2>Usuários cadastrados em todos os tenants.</h2>
        </div>
        <div class="item">
          <h1>Documentos</h1>
          <p>{{ totals.documents }}</p>
          <h2>Documentos criados em todos os tenants.</h2>
        </div>
      </template>
      <template v-else>
        <div class="item skeleton" v-for="n in 3" :key="n">
          <h1>&nbsp;</h1>
          <p>•••</p>
          <h2>&nbsp;</h2>
        </div>
      </template>
    </div>
    <section class="tenants-breakdown">
      <h1>Tenants</h1>
      <div class="tenants-title doc-grid">
        <label><h1>Nome</h1></label>
        <label><h1>Usuários</h1></label>
        <label><h1>Documentos</h1></label>
        <label><h1>Módulos ativos</h1></label>
        <label><h1>Ações</h1></label>
      </div>
      <div class="tenants-list doc-grid" v-for="tenant in tenants" :key="tenant.id">
        <label class="title">
          <h1>{{ tenant.name }}</h1>
          <h2>{{ tenant.acronym }}</h2>
        </label>
        <label><h1>{{ tenant.users_count }}</h1></label>
        <label><h1>{{ tenant.documents_count }}</h1></label>
        <label>
          <h1 v-if="tenant.modules?.length">{{ tenant.modules.map((m) => tenantModuleLabels[m as TenantModuleEnum] ?? m).join(', ') }}</h1>
          <h1 v-else>Nenhum</h1>
        </label>
        <label class="actions">
          <button class="tooltip" @click="$router.push({ name: 'platform-admin.tenants.edit', params: { id: tenant.id } })">
            <figure>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path fill='none' d="M13.2594 3.59924L5.04936 12.2892C4.73936 12.6192 4.43936 13.2692 4.37936 13.7192L4.00936 16.9592C3.87936 18.1292 4.71936 18.9292 5.87936 18.7292L9.09936 18.1792C9.54936 18.0992 10.1794 17.7692 10.4894 17.4292L18.6994 8.73924C20.1194 7.23924 20.7594 5.52924 18.5494 3.43924C16.3494 1.36924 14.6794 2.09924 13.2594 3.59924Z" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11.89 5.05078C12.0996 6.39164 12.7486 7.62492 13.735 8.55703C14.7214 9.48914 15.9894 10.0673 17.34 10.2008M3 22.0008H21"  stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </figure>
          </button>
        </label>
      </div>
      <SpinnerLoader v-if="!tenants" />
    </section>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useAxios } from '@/api/axios'
import { AxiosResponse } from 'axios'
import { TenantModel } from '@/models/TenantModel'
import { TenantModuleEnum, tenantModuleLabels } from '@/enums/Tenants/TenantModuleEnum'
import SpinnerLoader from '@/components/loader/SpinnerLoader.vue'

interface TenantWithCounts extends TenantModel {
  users_count: number
  documents_count: number
}

interface DashboardResponse {
  totals: { tenants: number, users: number, documents: number }
  tenants: TenantWithCounts[]
}

const axios = useAxios()
const totals = ref<DashboardResponse['totals']>()
const tenants = ref<TenantWithCounts[]>()

onMounted(() => {
  axios.get('/api/platform-admin/dashboard').then((res: AxiosResponse<DashboardResponse>) => {
    totals.value = res.data.totals
    tenants.value = res.data.tenants
  })
})
</script>

<style lang="scss">
  #platform-admin-dashboard{
    width: 100%;
    max-width: 1200px;
    display: flex;
    flex-direction: column;
    animation: enterContent ease .4s;
    header{
      margin: 0 0 32px 0;
      h1{
        color: #090909;
        font-family: 'Nunito', sans-serif;
        font-size: 32px;
        font-style: normal;
        font-weight: 800;
        line-height: normal;
        margin: 0 0 6px 0;
      }
      p{
        color: #6F6F6F;
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        margin: 0;
      }
    }
    .tiles{
      width: 100%;
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      column-gap: 20px;
      row-gap: 20px;
      margin: 0 0 48px 0;
      .item{
        width: 100%;
        min-width: 180px;
        height: 150px;
        display: flex;
        flex-direction: column;
        align-items: start;
        justify-content: center;
        border-radius: 32px;
        background: #E0ECE9;
        padding: 30px;
        box-sizing: border-box;
        &.skeleton{
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        h1{
          color: #090909;
          font-family: 'Inter', sans-serif;
          font-size: 16px;
          font-style: normal;
          font-weight: 700;
          line-height: normal;
          margin: 0;
        }
        p{
          color: #090909;
          font-family: 'Nunito', sans-serif;
          font-size: 44px;
          font-style: normal;
          font-weight: 900;
          line-height: normal;
          margin: 2px 0 1px 0;
        }
        h2{
          color: #090909;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-style: normal;
          font-weight: 500;
          line-height: normal;
          margin: 0;
          text-align: start;
        }
      }
    }
    .tenants-breakdown{
      width: 100%;
      display: flex;
      flex-direction: column;
      row-gap: 10px;
      > h1{
        color: #000;
        font-family: 'Inter', sans-serif;
        font-size: 12px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
        padding: 0 24px;
        margin: 0 0 4px 0;
        text-transform: uppercase;
      }
      .doc-grid{
        display: grid !important;
        grid-template-columns: 26% 15% 15% 1fr 68px;
        column-gap: 15px;
        justify-content: space-between;
        align-items: center;
        padding: 0 24px;
        box-sizing: border-box;
        label{
          display: flex;
          flex-direction: column;
          align-items: start;
          h1{
            color: #090909;
            font-family: 'Inter', sans-serif;
            font-size: 16px;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
            text-align: start;
            margin: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
          }
          h2{
            color: #6F6F6F;
            font-family: 'Inter', sans-serif;
            font-size: 13px;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
            margin: 6px 0 0 0;
          }
          &.title h1{
            -webkit-line-clamp: 2;
          }
          &.actions{
            button{
              width: 24px;
              min-width: 24px;
              height: 24px;
              min-height: 24px;
              cursor: pointer;
              figure{
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                margin: 0;
              }
              svg{
                width: 24px;
                min-width: 24px;
                height: 24px;
                min-height: 24px;
                stroke: #090909;
                transition: stroke .2s;
              }
              &:hover svg{
                stroke: $fluxoo-primary-color;
              }
            }
          }
        }
      }
      .tenants-title{
        margin: 0 0 -4px 0;
      }
      .tenants-list{
        width: 100%;
        height: 90px;
        border-radius: 24px;
        border: 1px solid #D3D3D3;
        background: #FFF;
        animation: enterContent ease-in-out .6s;
      }
    }
  }

  @media(max-width: 950px){
    #platform-admin-dashboard{
      .tiles{
        grid-template-columns: 1fr 1fr;
      }
      .tenants-breakdown{
        .doc-grid{
          grid-template-columns: 1fr 1fr 1fr 68px;
        }
        .tenants-title label:nth-child(4),
        .tenants-list label:nth-child(4){
          display: none;
        }
      }
    }
  }

  @media(max-width: 650px){
    #platform-admin-dashboard{
      .tiles{
        grid-template-columns: 1fr;
      }
    }
  }
</style>
